import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Benutzer } from '@beholder/shared';
import { api, ApiFehler } from './api';

interface SitzungsWert {
  ich: Benutzer | null;
  laedt: boolean;
  einrichtungNoetig: boolean;
  neuLaden(): Promise<void>;
  abmelden(): Promise<void>;
}

const Kontext = createContext<SitzungsWert | null>(null);

export function SitzungsAnbieter({ children }: { children: ReactNode }) {
  const [ich, setIch] = useState<Benutzer | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [einrichtungNoetig, setEinrichtungNoetig] = useState(false);

  const neuLaden = useCallback(async () => {
    try {
      setIch(await api.get<Benutzer>('/api/ich'));
      setEinrichtungNoetig(false);
    } catch (e) {
      setIch(null);
      if (e instanceof ApiFehler && e.status === 401) {
        const r = await api.get<{ noetig: boolean }>('/api/einrichtung').catch(() => ({ noetig: false }));
        setEinrichtungNoetig(r.noetig);
      }
    } finally {
      setLaedt(false);
    }
  }, []);

  const abmelden = useCallback(async () => {
    await api.post('/api/abmelden').catch(() => undefined);
    setIch(null);
  }, []);

  useEffect(() => {
    void neuLaden();
    const weg = () => setIch(null);
    const pw = () => void neuLaden();
    window.addEventListener('beholder:abgemeldet', weg);
    window.addEventListener('beholder:passwort', pw);
    return () => {
      window.removeEventListener('beholder:abgemeldet', weg);
      window.removeEventListener('beholder:passwort', pw);
    };
  }, [neuLaden]);

  return <Kontext.Provider value={{ ich, laedt, einrichtungNoetig, neuLaden, abmelden }}>{children}</Kontext.Provider>;
}

export function useSitzung(): SitzungsWert {
  const k = useContext(Kontext);
  if (!k) throw new Error('useSitzung außerhalb von SitzungsAnbieter');
  return k;
}

/** Nur für angemeldete Bereiche: liefert den Benutzer sicher. */
export function useIch(): Benutzer {
  const { ich } = useSitzung();
  if (!ich) throw new Error('nicht angemeldet');
  return ich;
}
